import React from "react";

import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

import { IconButton, TextButton } from "@/components/Button";
import { Status } from "@/components/Chip/Status";
import { Spacer } from "@/components/Spacer/Spacer";
import { Body, BodyBold, Label, SubheadBold } from "@/components/Typography";
import { ClientFragment } from "@/gql/graphql";
import { colors } from "@/theme/overrides/colors";
import { format } from "@/utils/datetime";
import DumbellIcon from "@components/Icons/DumbellIcon";
import ScaleIcon from "@components/Icons/ScaleIcon";
import WaterIntakeIcon from "@components/Icons/WaterIntakeIcon";

import MealTypeIcon from "../../Clients/ClientDetails/ClientDiary/Meals/components/MealTypeIcon";

const useStyles = makeStyles()({
  timelineItem: {
    "&:before": {
      display: "none",
    },
  },
  timelineDot: {
    border: "2px solid #DEE3EA",
    height: "38px",
    width: "38px",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 0,
    marginTop: 0,
  },
  px20: {
    paddingLeft: "20px",
    paddingRight: "20px",
  },
});

interface ClientItemProps {
  client: ClientFragment;
  status: string;
}

export const ClientItem: React.FC<ClientItemProps> = ({ client, status }) => {
  const navigate = useNavigate();
  const { classes } = useStyles();

  // Functions
  const getActivityIcon = (
    activity: ClientFragment["activities"]["nodes"][0]
  ) => {
    switch (activity?.notifiableType) {
      case "Meal":
        return (
          <MealTypeIcon type={activity.params?.meal_type?.toUpperCase()} />
        );
      case "Exercise":
        return <DumbellIcon />;
      case "WaterIntake":
        return <WaterIntakeIcon />;
      case "BodyComposition":
      case "BodyMeasurement":
        return <ScaleIcon fontSize="large" />;
      case "DietaryHistory":
        return <RestaurantIcon fontSize="large" />;
    }
  };

  const renderClientStatus = () => {
    switch (status) {
      case "inactive":
        return <Status label="Inactive" color="secondary" />;
      case "struggling":
        return <Status label="Struggling" color="warning" />;
      case "active":
      default:
        return <Status label="Active" />;
    }
  };

  const renderActivities = () => {
    if ((client?.activities.nodes.length ?? 0) < 0) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Body text="This client does not have any activities yet." />
          <TextButton
            text="View profile"
            onClick={() => navigate(`/clients/${client?.id}`)}
          />
        </Box>
      );
    }

    return (
      <Box>
        {client?.activities.nodes.map((activity, i) => (
          <TimelineItem className={classes.timelineItem} key={activity?.id}>
            <TimelineSeparator>
              <TimelineDot className={classes.timelineDot} variant="outlined">
                {getActivityIcon(activity)}
              </TimelineDot>
              {i < client.activities.nodes.length - 1 && <TimelineConnector />}
            </TimelineSeparator>
            <TimelineContent>
              <SubheadBold text={activity.message} />
              <Label text={format({ datetime: activity.createdAt }, "Pp")} />
            </TimelineContent>
          </TimelineItem>
        ))}
        <TextButton
          text="View profile"
          onClick={() => navigate(`/clients/${client?.id}`)}
        />
      </Box>
    );
  };

  return (
    <Accordion>
      <AccordionSummary
        className={classes.px20}
        expandIcon={
          <IconButton
            onClick={function (): void {
              throw new Error("Function not implemented.");
            }}
            icon={<MoreHorizIcon color="primary" />}
          />
        }
      >
        <Box display="flex" width="100%" alignItems="center">
          <Box display="flex" alignItems="center" flex={1}>
            <Avatar src={client?.profile?.avatarUrl ?? undefined} />
            <Spacer w={8} />
            <BodyBold
              color={colors.mapped.surface.default}
              text={client?.profile?.fullName}
            />
          </Box>
          <Spacer w={20} />
          <Divider orientation="vertical" />
          <Spacer w={20} />
          <Box flex={1}>
            <Body
              text={client?.programForDate?.name ?? "No plan assigned yet"}
            />
            <Spacer h={8} />
            <Box display="flex">
              <Box display="flex" flexDirection="row">
                <SubheadBold text="Start:" />
                <SubheadBold text={client?.programForDate?.startDate ?? "--"} />
              </Box>
              <Spacer w={2} />
              <Box display="flex" flexDirection="row">
                <SubheadBold text="End:" />
                <SubheadBold text={client?.programForDate?.endDate ?? "--"} />
              </Box>
            </Box>
          </Box>
          <Spacer w={20} />
          <Divider orientation="vertical" />
          <Spacer w={20} />
          <Box flex={1}>{renderClientStatus()}</Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Timeline position="right">{renderActivities()}</Timeline>
      </AccordionDetails>
    </Accordion>
  );
};
